import React from 'react';

import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router';

import { Field, Form } from '@hrlogics/react-components/form';

import { appRoutes } from '@clearverify/constants';

import {
  useUserRegistrationVerifierAdminMutation,
  useUserRegistrationVerifierManagerMutation,
} from 'src/api';
import { NOTICE_TO_FURNISHERS } from 'src/assets';
import { Button, PdfRenderer } from 'src/components/common';
import { CheckboxField } from 'src/components/finalForm';
import { useNotification } from 'src/hooks/app';
import { useAppAuth, useAuthSignupVerifier } from 'src/hooks/auth';
import type { SxProp } from 'src/types/mui';
import { validateWithYupSchema } from 'src/utils/yup';

import { useCheckIsAdditionalVerifierAdmin } from '../../hooks';
import {
  NoticeToFurnishersFormFields,
  NoticeToFurnishersFormValues,
  RelatedVerifier,
} from '../../types';
import {
  noticeToFurnishersFormValidationSchema,
  prepareFormDataVerifierAdmin,
  prepareFormDataVerifierManager,
} from '../../utils';
import { proceedId } from '../constants';

export const NoticeToFurnishersForm: React.FC = () => {
  const navigate = useNavigate();

  const { formData, goToNextStep } = useAuthSignupVerifier();
  const { setAuthState } = useAppAuth();
  const { spawnNotification } = useNotification();

  const [registerAdmin] = useUserRegistrationVerifierAdminMutation();
  const [registerManager] = useUserRegistrationVerifierManagerMutation();
  const currentCompany = formData.currentCompany as RelatedVerifier | undefined;
  const [checkIsAdditionalVerifierAdmin] = useCheckIsAdditionalVerifierAdmin(currentCompany?.id);

  const handleRegisterAdmin = React.useCallback(async () => {
    try {
      const { data } = await registerAdmin({
        variables: prepareFormDataVerifierAdmin(formData),
      });
      await setAuthState({
        bearerToken: String(data?.userRegistrationVerifierAdmin.idToken),
        accessToken: data?.userRegistrationVerifierAdmin.accessToken ?? undefined,
      });
      navigate(appRoutes.auth.setupMfa);
    } catch (error) {
      console.error(error);
      spawnNotification('Internet connection is unstable', 'error');
    }
  }, [formData, navigate, registerAdmin, setAuthState, spawnNotification]);

  const handleRegisterManager = React.useCallback(async () => {
    try {
      const { data } = await registerManager({
        variables: await prepareFormDataVerifierManager(formData),
      });
      await setAuthState({
        bearerToken: String(data?.userRegistrationVerifierManager.idToken),
        accessToken: data?.userRegistrationVerifierManager.accessToken ?? undefined,
      });
      navigate(appRoutes.auth.setupMfa);
    } catch {
      spawnNotification('Internet connection is unstable', 'error');
    }
  }, [formData, navigate, registerManager, setAuthState, spawnNotification]);

  const handleSubmit = React.useCallback(async () => {
    if (currentCompany?.id && currentCompany.id !== proceedId) {
      const { data } = await checkIsAdditionalVerifierAdmin();
      const isAdditionalVerifierAdmin =
        data?.checkIsAdditionalVerifierAdmin.isAdditionalVerifierAdmin;

      const haveVerifierBasicInfo = Boolean(formData.industry);

      if (isAdditionalVerifierAdmin && haveVerifierBasicInfo) {
        goToNextStep();
      } else if (haveVerifierBasicInfo) {
        await handleRegisterAdmin();
      } else {
        await handleRegisterManager();
      }
    } else {
      await handleRegisterAdmin();
    }
  }, [
    checkIsAdditionalVerifierAdmin,
    currentCompany?.id,
    formData,
    goToNextStep,
    handleRegisterAdmin,
    handleRegisterManager,
  ]);

  return (
    <Box>
      <Typography variant="body2" fontSize="24px">
        Notice to Furnishers
      </Typography>

      <Typography variant="body2" fontSize="16px" mt={1}>
        Please read the Notice to Furnishers document below and acknowledge once you have finished
        reviewing.
      </Typography>

      <Box mt={2}>
        <PdfRenderer src={NOTICE_TO_FURNISHERS} />
      </Box>

      <Form<NoticeToFurnishersFormValues>
        onSubmit={handleSubmit}
        subscription={{ valid: true, values: true, submitting: true, errors: true }}
        containerProps={{ mt: 4 }}
        validate={validateWithYupSchema(noticeToFurnishersFormValidationSchema)}
      >
        {({ submitting, valid }) => {
          return (
            <React.Fragment>
              <Field
                component={CheckboxField}
                name={NoticeToFurnishersFormFields.isRulesRead}
                label="I acknowledge that I have read, understand, and agree to the Notice to Furnishers"
                fullWidth
                required
              />

              <Box display="flex" justifyContent="center" mt={4}>
                <Button
                  type="submit"
                  sx={submitButtonStyles}
                  disabled={submitting || !valid}
                  loading={submitting}
                  size="large"
                  variant="contained"
                >
                  Submit
                </Button>
              </Box>
            </React.Fragment>
          );
        }}
      </Form>
    </Box>
  );
};

const submitButtonStyles: SxProp = {
  px: 5,
};
